import { useState } from "react";
import { Broadcast } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, errMsg, useFetch } from "@/lib/api";
import { usd, num } from "@/lib/format";

export function PlaidActivity({ items }) {
  const { data, reload } = useFetch("/plaid/activity");
  const [busy, setBusy] = useState(false);
  const linked = items?.items || [];
  const allOn = linked.length > 0 && linked.every((i) => i.webhook_url === items.webhook_url);
  const enable = async () => {
    setBusy(true);
    try {
      const r = await api.post("/plaid/items/enable-webhooks");
      r.data.errors?.length ? toast.error(r.data.errors[0]) : toast.success(`Auto-updates on for ${r.data.updated} connection(s)`);
      items.reload?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="plaid-activity">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">When Plaid reports new holdings or trades, balances refresh on their own and a new snapshot is recorded. New connections get auto-updates automatically.</p>
        {linked.length > 0 && (allOn
          ? <Badge className="bg-emerald-600 hover:bg-emerald-600 gap-1" data-testid="auto-update-status"><Broadcast size={12} /> Auto-updates on</Badge>
          : <Button size="sm" variant="outline" onClick={enable} disabled={busy} data-testid="enable-auto-updates-button">{busy ? "Enabling..." : "Enable auto-updates"}</Button>)}
        {!linked.length && <Badge variant="outline" data-testid="auto-update-status">No Plaid connections yet</Badge>}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="flex justify-between items-center mb-2"><p className="text-xs uppercase tracking-wider text-muted-foreground">Update events</p><Button size="sm" variant="ghost" onClick={reload} data-testid="activity-refresh-button">Refresh</Button></div>
          <ul className="text-sm divide-y divide-border/60" data-testid="webhook-events-list">
            {data?.events?.map((e, i) => (
              <li key={i} className="py-2 flex justify-between gap-3"><span>{e.webhook_type === "HOLDINGS" ? "Holdings updated" : "New trades"}{e.institution ? ` · ${e.institution}` : ""}</span><span className="text-xs text-muted-foreground font-mono-num">{e.status} · {new Date(e.received_at).toLocaleString()}</span></li>
            ))}
            {!data?.events?.length && <li className="py-2 text-muted-foreground">No updates received yet.</li>}
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Recent trades (last 30 days)</p>
          <ul className="text-sm divide-y divide-border/60" data-testid="plaid-transactions-list">
            {data?.transactions?.map((t) => (
              <li key={t.investment_transaction_id} className="py-2 flex justify-between gap-3"><span className="truncate">{t.date} · {t.security || t.name} · {t.subtype}</span><span className="font-mono-num shrink-0">{num(t.quantity, 3)} @ {usd(t.price, 2)}</span></li>
            ))}
            {!data?.transactions?.length && <li className="py-2 text-muted-foreground">No trades reported yet.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
